import { useState, useCallback } from "react";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { TimekeeperService } from "services/TimekeeperService";
import { Grant } from "repository/timekeeper/@types/Grant";
import { AccrualCode } from "repository/timekeeper/@types/AccrualCode";
import { Paycode } from "repository/timekeeper/@types/PaycodeModel";
import { DatePattern } from "repository/timekeeper/@types/DatePattern";
import { LimitWithId } from "repository/timekeeper/@types/Limit";

const timekeeperService = new TimekeeperService();

export interface AvailableData {
    grants: Grant[];
    limits: LimitWithId[];
    paycodes: Paycode[];
    accrualCodes: AccrualCode[];
    datePatterns: DatePattern[];
}

export interface LoadingState {
    grants: boolean;
    limits: boolean;
    paycodes: boolean;
    accrualCodes: boolean;
    datePatterns: boolean;
}

const LOADING_ALL_TRUE: LoadingState = {
    grants: true, limits: true, paycodes: true, accrualCodes: true, datePatterns: true,
};

const LOADING_ALL_FALSE: LoadingState = {
    grants: false, limits: false, paycodes: false, accrualCodes: false, datePatterns: false,
};

export const useAccrualRuleData = () => {
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const [availableData, setAvailableData] = useState<AvailableData>({
        grants: [], limits: [], paycodes: [], accrualCodes: [], datePatterns: [],
    });
    const [loadingStates, setLoadingStates] = useState<LoadingState>(LOADING_ALL_TRUE);

    const loadData = useCallback(async (): Promise<{ firstGrantId: number; firstLimitId: number; firstPaycodeId: number; }> => {
        setLoadingStates(LOADING_ALL_TRUE);

        const [grants, limits, paycodes, accrualCodes, datePatterns] = await Promise.allSettled([
            timekeeperService.grants.getAll(),
            timekeeperService.limits.getAll(),
            timekeeperService.paycodes.getPaycodes(),
            timekeeperService.accrual_code.getAll(),
            timekeeperService.date_pattern.getAll(),
        ]);

        let firstGrantId = 0;
        let firstLimitId = 0;
        let firstPaycodeId = 0;

        setAvailableData((prev) => {
            const next = { ...prev };

            if (grants.status === "fulfilled") {
                next.grants = grants.value;
                firstGrantId = grants.value[0]?.grant_id ?? 0;
            }

            if (limits.status === "fulfilled") {
                next.limits = limits.value as LimitWithId[];
                firstLimitId = limits.value[0]?.limit_id ?? 0;
            }

            if (paycodes.status === "fulfilled") {
                next.paycodes = paycodes.value;
                firstPaycodeId = paycodes.value[0]?.paycode_id ?? 0;
            }

            if (accrualCodes.status === "fulfilled") {
                next.accrualCodes = accrualCodes.value;
            }

            if (datePatterns.status === "fulfilled") {
                next.datePatterns = datePatterns.value;
            }

            return next;
        });

        setLoadingStates(LOADING_ALL_FALSE);

        const hasErrors = [grants, limits, paycodes, accrualCodes, datePatterns].some(result => result.status === "rejected");

        if (hasErrors) {
            enqueueSnackbar(t("general.alerts.error_loading_data"), { variant: "error" });
        }

        return { firstGrantId, firstLimitId, firstPaycodeId };

    }, [enqueueSnackbar, t]);

    return { availableData, loadingStates, loadData };
}